import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, Trash2, X, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 30000;

const formatRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins} min atrás`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d atrás`;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
};

const NotificationBell = ({ onNavigateToCRM }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const previousCountRef = useRef(0);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=30`),
        axios.get(`${API}/notifications/unread-count`),
      ]);
      setItems(listRes.data || []);
      const count = countRes.data?.count || 0;

      // Subtle audio chime when a brand new lead arrives (only after first fetch)
      if (previousCountRef.current > 0 && count > previousCountRef.current) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 880;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
          osc.start();
          osc.stop(ctx.currentTime + 0.4);
        } catch (_) { /* ignore audio errors */ }
      }
      previousCountRef.current = count;
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleItemClick = async (notif) => {
    if (!notif.read) await handleMarkRead(notif.id);
    if (notif.type === 'lead_created' && onNavigateToCRM) {
      onNavigateToCRM();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        data-testid="notification-bell-btn"
        onClick={() => setOpen((v) => !v)}
        className="relative text-stone-400 hover:text-white p-2 transition-colors"
        title="Notificações"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[92vw] sm:w-96 max-w-md bg-stone-800 border border-stone-700 rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-700 bg-gradient-to-r from-amber-600/20 to-orange-600/10">
            <div>
              <h3 className="text-white font-bold text-sm">Notificações</h3>
              <p className="text-stone-400 text-xs">
                {unreadCount > 0 ? `${unreadCount} não lida(s)` : 'Tudo em dia'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  data-testid="notification-mark-all-read-btn"
                  onClick={handleMarkAllRead}
                  className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded"
                  title="Marcar todas como lidas"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-stone-400 hover:text-white p-1 rounded"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 text-stone-600 mx-auto mb-2" />
                <p className="text-stone-400 text-sm">Sem notificações ainda.</p>
                <p className="text-stone-500 text-xs mt-1">Você será avisado aqui quando um novo lead se qualificar.</p>
              </div>
            ) : (
              items.map((notif) => (
                <div
                  key={notif.id}
                  data-testid={`notification-item-${notif.id}`}
                  className={`group flex items-start gap-3 px-4 py-3 border-b border-stone-700/50 cursor-pointer transition-colors ${
                    notif.read ? 'bg-transparent hover:bg-stone-700/30' : 'bg-amber-500/5 hover:bg-amber-500/10'
                  }`}
                  onClick={() => handleItemClick(notif)}
                >
                  <div className={`mt-0.5 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                    notif.read ? 'bg-stone-700' : 'bg-amber-500/20'
                  }`}>
                    <UserPlus className={`w-4 h-4 ${notif.read ? 'text-stone-400' : 'text-amber-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-semibold truncate ${notif.read ? 'text-stone-300' : 'text-white'}`}>
                        {notif.title}
                      </p>
                      {!notif.read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-1.5" />
                      )}
                    </div>
                    {notif.message && (
                      <p className="text-stone-400 text-xs mt-0.5 line-clamp-2">{notif.message}</p>
                    )}
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-stone-500 text-[11px]">{formatRelative(notif.createdAt)}</span>
                      <button
                        data-testid={`notification-delete-${notif.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(notif.id); }}
                        className="text-stone-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Excluir"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
